import { Injectable, inject } from '@angular/core';
import {
  UntypedFormBuilder,
  Validators,
  UntypedFormGroup
} from '@angular/forms';
import { QuestionBase } from './classes/question-base';
import { CheckboxQuestion } from './classes/question-checkbox';
import { DatetimeQuestion } from './classes/question-datetime';
import { DropdownQuestion } from './classes/question-dropdown';
import { NestedGroupQuestion } from './classes/question-nestedGroup';
import { RadioQuestion } from './classes/question-radio';
import { RangeQuestion } from './classes/question-range';
import { TextAreaQuestion } from './classes/question-textarea';
import { TextboxQuestion } from './classes/question-textbox';
import { DynamicFormInputQuestion } from './dynamic-form.models';

@Injectable()
export class DynamicFormQuestionControlService {
  private fb = inject(UntypedFormBuilder);

  // Return questions array of  descriptions based on the Store
  questionBuilder(questions: DynamicFormInputQuestion[]): QuestionBase<any>[] {
    const _q = questions.map(question => {
      let nestedQuestions: QuestionBase<any>[];
      const {
        key,
        title: label,
        type,
        order,
        class_id,
        required,
        customClasses,
        readonly,
        display,
        conditionnalRules
      } = question;
      switch (type) {
        case null:
          if (class_id === 'filter-user-is-in-user-status') {
            nestedQuestions = question.options.map(option => {
              return new CheckboxQuestion({
                key: option.name,
                label: option.value,
                type,
                color: option.color
              });
            });
            return new NestedGroupQuestion({
              key,
              label,
              type: 'nestedGroupQuestion',
              nestedQuestions,
              display
            });
          }
          return;

        case 'date':
          const { date_format: displayFormat = 'YYYY-MM-DD' } = question;
          return new DatetimeQuestion({
            key,
            label,
            displayFormat,
            pickerFormat: displayFormat,
            customClasses,
            display,
            conditionnalRules
          });

        case 'time':
          return new DatetimeQuestion({
            key,
            label,
            displayFormat: 'HH:mm',
            pickerFormat: 'HH:mm',
            customClasses,
            display,
            conditionnalRules
          });

        case 'range':
          const { min, max, color, startLabel, endLabel, dualKnobs } = question;
          return new RangeQuestion({
            key,
            label,
            type,
            order,
            min,
            max,
            color,
            startLabel,
            endLabel,
            dualKnobs,
            display,
            conditionnalRules
          });

        case 'radio':
          const options = question.options.map(option => {
            return {
              key: option.value,
              value: option.name
            };
          });
          return new RadioQuestion({
            key,
            label,
            type,
            order,
            options,
            customClasses,
            readonly,
            display,
            conditionnalRules
          });

        case 'checkbox':
          if (question.options) {
            nestedQuestions = this.forgeNestedCheckbox(question.options);
            return new NestedGroupQuestion({
              key,
              label,
              type: 'nestedGroupQuestion',
              nestedQuestions,
              customClasses,
              conditionnalRules,
              display
            });
          }
          return new CheckboxQuestion({ key, label, type, order });

        case 'double_datetime_picker':
          nestedQuestions = [
            new DatetimeQuestion({
              key: 'startDate',
              label: 'FROM',
              displayFormat: question.date_format,
              pickerFormat: question.date_format
            }),
            new DatetimeQuestion({
              key: 'endDate',
              label: 'TO',
              displayFormat: question.date_format,
              pickerFormat: question.date_format
            })
          ];
          return new NestedGroupQuestion({
            key,
            label,
            type: 'nestedGroupQuestion',
            nestedQuestions,
            customClasses,
            display,
            conditionnalRules
          });

        case 'select':
          return new DropdownQuestion({
            key,
            label,
            type,
            options: question.options,
            customClasses,
            display,
            conditionnalRules
          });

        case 'textarea':
          return new TextAreaQuestion({
            key,
            label,
            type,
            customClasses,
            display,
            conditionnalRules
          });

        default:
          /* If an input uses an unknown type
          it will be rendered as a text input
          */

          return new TextboxQuestion({
            key,
            label,
            type,
            order,
            required,
            customClasses,
            display,
            conditionnalRules
          });
      }
    });
    return _q;
  }
  // create a formGroup and formControls based on the questions list
  createFormGroup(questions: QuestionBase<any>[]): UntypedFormGroup {
    const form = questions.reduce((prev, current) => {
      switch (current.controlType) {
        case 'nestedGroupQuestion':
          const formControls = current['nestedQuestions'].map(q => {
            return this.fb.control('');
          });
          prev[current['key']] = this.fb.array(formControls);
          return prev;
        default:
          prev[current['key']] = current.required
            ? ['', Validators.required]
            : [''];
          return prev;
      }
    }, {});
    return this.fb.group(form);
  }

  setFormValue(value, questions: QuestionBase<any>[]) {
    return questions.reduce((_form, question) => {
      const { key: questionKey, controlType } = question;
      const currentValue = value === null ? '' : value[questionKey];

      switch (controlType) {
        case 'range':
          _form[questionKey] = this.parseRangeQuestion(question, currentValue);
          break;

        case 'nestedGroupQuestion':
          _form[questionKey] = this.parseNestedQuestion(question, currentValue);
          break;

        default:
          _form[questionKey] = currentValue;
          break;
      }

      return _form;
    }, {});
  }

  checkConditionnals(formValues, questions) {
    const _questions = [...questions];

    for (const question of _questions) {
      if (!!question.conditionnalRules) {
        const parentQuestionsKeys = Object.keys(question.conditionnalRules);

        for (const key of parentQuestionsKeys) {
          const isInConditionArray = question.conditionnalRules[key].includes(
            formValues[key]
          );
          if (isInConditionArray) {
            question.display = true;
          } else if (!isInConditionArray && question.display) {
            question.display = !question.display;
          }
        }
      }
    }
    return _questions;
  }

  addInvalid(
    questionsId: Array<string>,
    questions: QuestionBase<any>[]
  ): QuestionBase<any>[] {
    const invalidClassString = 'dynamic-form__has-error';
    return questions.map(question => ({
      ...question,
      customClasses: questionsId.includes(question.key)
        ? [...question.customClasses, invalidClassString]
        : question.customClasses.filter(
            classString => classString !== invalidClassString
          )
    }));
  }

  private forgeNestedCheckbox(options: any) {
    return options.map(option => {
      const { name: key, value: label } = option;
      return new CheckboxQuestion({
        key,
        label,
        type: 'checkbox'
      });
    });
  }

  // We need to setup default min - max default values
  private parseRangeQuestion(question, value) {
    if (question.dualKnobs === true) {
      const _value = { lower: question.min || 0, upper: question.max };
      if (Array.isArray(value)) {
        _value.lower = value[0];
        _value.upper = value[1];
      }
      return _value;
    }
    return value || question.min;
  }

  private parseNestedQuestion(question, value) {
    const { nestedQuestions } = question;
    return this.parseNestedGroup(nestedQuestions, value);
  }

  private parseNestedGroup(questions, value) {
    return questions.map((question, index) => {
      const { controlType } = question;
      let _value;
      switch (controlType) {
        case 'checkbox':
          _value = value === undefined ? false : value[index];
          break;

        case 'datetime':
          _value = value === undefined ? '' : value[index];
          break;
      }
      return _value;
    });
  }
}
